import type { SearchTerminalContext } from "@wags/discovery/index.ts";
import type { FindSearch } from "../routes/find-route.contract.ts";

const FIND_EFFECTIVE_SEARCH_KEYS = [
    "q",
    "lat",
    "lng",
    "maxDistanceMiles",
    "hardRadius",
    "breedSlugs",
    "dogName",
    "breedType",
    "providerName",
    "providerType",
    "size",
    "coatType",
    "sheddingLevel",
    "hypoallergenic",
    "priceMin",
    "priceMax",
    "dobMin",
    "dobMax",
    "sex",
    "friendlinessChildren",
    "friendlinessDogs",
    "friendlinessCats",
    "energyLevel",
    "trainability",
    "healthIssuesRisk",
    "sortBy",
    "bestMatchPreferences",
] as const satisfies readonly (keyof FindSearch)[];

type FindSearchKey = keyof FindSearch;
type FindSearchValue = FindSearch[FindSearchKey];

function normalizeSearchValue(value: FindSearchValue): string {
    if (value === undefined) return "undefined";
    if (Array.isArray(value)) {
        return JSON.stringify(value.map(normalizeSearchValue).sort());
    }
    if (typeof value !== "object" || value === null) {
        return JSON.stringify(value);
    }
    const entries = Object.entries(value)
        .filter(([, nested]) => nested !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, normalizeSearchValue(nested)]);
    return JSON.stringify(Object.fromEntries(entries));
}

function searchValueKey(value: FindSearchValue) {
    return normalizeSearchValue(value);
}

function effectiveSearchKey(search: FindSearch) {
    return JSON.stringify(
        FIND_EFFECTIVE_SEARCH_KEYS.map((key) => [
            key,
            searchValueKey(
                key === "sortBy"
                    ? (search.sortBy ?? "best-match")
                    : search[key],
            ),
        ]),
    );
}

export function hasEffectiveFindSearchChange(
    currentSearch: FindSearch,
    nextSearch: FindSearch,
) {
    return effectiveSearchKey(currentSearch) !== effectiveSearchKey(nextSearch);
}

export function findSearchStateAfterChange(
    renderedSearch: FindSearch,
    requestedSearch: FindSearch,
    pendingSearch: FindSearch,
): FindSearch {
    const mergedSearch: FindSearch = { ...pendingSearch };
    const changedKeys = new Set<FindSearchKey>([
        ...Object.keys(renderedSearch),
        ...Object.keys(requestedSearch),
    ] as FindSearchKey[]);
    for (const key of changedKeys) {
        if (
            searchValueKey(renderedSearch[key]) ===
            searchValueKey(requestedSearch[key])
        ) {
            continue;
        }
        const value = requestedSearch[key];
        if (value === undefined) {
            delete mergedSearch[key];
        } else {
            setFindSearchValue(mergedSearch, key, value);
        }
    }
    return mergedSearch;
}

function setFindSearchValue<K extends FindSearchKey>(
    search: FindSearch,
    key: K,
    value: FindSearch[K],
) {
    search[key] = value;
}

export function findSearchParamsForCommittedChange(
    search: FindSearch,
): FindSearch {
    return {
        ...search,
        dogId: undefined,
        feedRow: undefined,
        page: 0,
    };
}

export function createFindFilterSubmissionDraft(
    previousContext: Omit<SearchTerminalContext, "submissionId">,
    currentSearch: FindSearch,
    nextSearch: FindSearch,
): Omit<SearchTerminalContext, "submissionId"> | null {
    if (!hasEffectiveFindSearchChange(currentSearch, nextSearch)) return null;
    return {
        searchInteractionId: previousContext.searchInteractionId,
        queryRevision: previousContext.queryRevision + 1,
        parseId: previousContext.parseId,
        query: previousContext.query,
        surface: previousContext.surface,
        submissionSource: "filters",
        action: "filter_change",
    };
}
